import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import type { StreamServer } from '../types';
import { loadServers } from '../storage/streamPlayerStorage';
import { normalizeServerUrl, serversDuplicate } from '../utils/serverFormUtils';
import { newStreamServer } from '../utils/newStreamServer';
import { COUNTRY_SELECT_OPTIONS } from '../utils/countryLabels';
import { streamGhostOnDarkHover, streamOutlineOnDarkHover } from '../darkThemeButtonProps';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (server: StreamServer, options: { connect: boolean }) => void;
}

type AddKind = 'xtream' | 'm3u_url';

const DEFAULT_RESTRICT_COUNTRY = true;
const DEFAULT_COUNTRY_CODE = 'CA';

export function AddServerModal({ isOpen, onClose, onSave }: Props) {
  const [addKind, setAddKind] = useState<AddKind>('xtream');
  const [name, setName] = useState('');
  const [server, setServer] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [restrictCountry, setRestrictCountry] = useState(DEFAULT_RESTRICT_COUNTRY);
  const [connectionCountryCode, setConnectionCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setAddKind('xtream');
    setName('');
    setServer('');
    setUsername('');
    setPassword('');
    setShowPassword(false);
    setRestrictCountry(DEFAULT_RESTRICT_COUNTRY);
    setConnectionCountryCode(DEFAULT_COUNTRY_CODE);
    setErrors({});
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Name is required.';
    if (!server.trim()) {
      e.server = addKind === 'm3u_url' ? 'Playlist URL is required.' : 'Server URL is required.';
    }

    if (addKind === 'xtream') {
      if (!username.trim()) e.username = 'Username is required.';
      if (!password) e.password = 'Password is required.';
    }

    if (restrictCountry && !connectionCountryCode.trim()) {
      e.connectionCountryCode = 'Select a country when restriction is enabled.';
    }

    const norm = normalizeServerUrl(server);
    if (Object.keys(e).length === 0) {
      const dupUser = addKind === 'm3u_url' ? '' : username.trim();
      if (serversDuplicate(loadServers(), norm, dupUser)) {
        e.server =
          addKind === 'm3u_url'
            ? 'A server with this playlist URL already exists.'
            : 'A server with this URL and username already exists.';
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const build = (): StreamServer => {
    const norm = normalizeServerUrl(server);
    if (addKind === 'm3u_url') {
      return newStreamServer({
        name: name.trim(),
        server: norm,
        username: '',
        password: '',
        sourceKind: 'm3u_url',
        restrictConnectionToCountry: restrictCountry,
        connectionCountryCode: restrictCountry ? connectionCountryCode : undefined,
      });
    }
    return newStreamServer({
      name: name.trim(),
      server: norm,
      username: username.trim(),
      password,
      restrictConnectionToCountry: restrictCountry,
      connectionCountryCode: restrictCountry ? connectionCountryCode : undefined,
    });
  };

  const handleSave = async (connect: boolean) => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await Promise.resolve(onSave(build(), { connect }));
      reset();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" onCloseComplete={reset}>
      <ModalOverlay />
      <ModalContent bg="#121a22" borderWidth="1px" borderColor="whiteAlpha.200">
        <ModalHeader color="white" fontSize="md">
          Add server
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody>
          <Tabs
            variant="soft-rounded"
            colorScheme="teal"
            index={addKind === 'xtream' ? 0 : 1}
            onChange={(i) => setAddKind(i === 0 ? 'xtream' : 'm3u_url')}
            mb={3}
          >
            <TabList bg="whiteAlpha.100" borderRadius="md" p={1}>
              <Tab flex={1} fontSize="sm" color="whiteAlpha.800" _selected={{ color: 'white', bg: 'teal.600' }}>
                Xtream Codes
              </Tab>
              <Tab flex={1} fontSize="sm" color="whiteAlpha.800" _selected={{ color: 'white', bg: 'teal.600' }}>
                M3U / M3U8 URL
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0} pt={3}>
                <Text fontSize="xs" color="whiteAlpha.500" mb={3}>
                  Xtream Codes API — panel URL, username, and password.
                </Text>
              </TabPanel>
              <TabPanel px={0} pt={3}>
                <Text fontSize="xs" color="whiteAlpha.500" mb={3}>
                  Simple playlist — enter a display name and the full URL to your .m3u or .m3u8 file (no login).
                </Text>
              </TabPanel>
            </TabPanels>
          </Tabs>
          <VStack spacing={3} align="stretch">
            <FormControl isInvalid={Boolean(errors.name)}>
              <FormLabel color="whiteAlpha.800" fontSize="sm">
                Name
              </FormLabel>
              <Input
                value={name}
                onChange={(ev) => setName(ev.target.value)}
                placeholder={addKind === 'm3u_url' ? 'My playlist' : 'My home IPTV'}
                size="sm"
                bg="whiteAlpha.100"
                borderColor="whiteAlpha.200"
                color="white"
              />
              <FormErrorMessage>{errors.name}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={Boolean(errors.server)}>
              <FormLabel color="whiteAlpha.800" fontSize="sm">
                {addKind === 'm3u_url' ? 'Playlist URL' : 'Server URL'}
              </FormLabel>
              <Input
                value={server}
                onChange={(ev) => setServer(ev.target.value)}
                placeholder={
                  addKind === 'm3u_url'
                    ? 'https://example.com/live/playlist.m3u8'
                    : 'https://example.com:8080'
                }
                size="sm"
                bg="whiteAlpha.100"
                borderColor="whiteAlpha.200"
                color="white"
              />
              <FormErrorMessage>{errors.server}</FormErrorMessage>
            </FormControl>
            {addKind === 'xtream' && (
              <>
                <FormControl isInvalid={Boolean(errors.username)}>
                  <FormLabel color="whiteAlpha.800" fontSize="sm">
                    Username
                  </FormLabel>
                  <Input
                    value={username}
                    onChange={(ev) => setUsername(ev.target.value)}
                    size="sm"
                    bg="whiteAlpha.100"
                    borderColor="whiteAlpha.200"
                    color="white"
                  />
                  <FormErrorMessage>{errors.username}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={Boolean(errors.password)}>
                  <FormLabel color="whiteAlpha.800" fontSize="sm">
                    Password
                  </FormLabel>
                  <InputGroup size="sm">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(ev) => setPassword(ev.target.value)}
                      pr="2.5rem"
                      bg="whiteAlpha.100"
                      borderColor="whiteAlpha.200"
                      color="white"
                    />
                    <InputRightElement w="2.5rem" h="32px">
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        icon={
                          showPassword ? <MdVisibilityOff size={18} /> : <MdVisibility size={18} />
                        }
                        size="xs"
                        variant="ghost"
                        color="whiteAlpha.600"
                        {...streamGhostOnDarkHover}
                        onClick={() => setShowPassword((v) => !v)}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{errors.password}</FormErrorMessage>
                </FormControl>
              </>
            )}
            <FormControl>
              <Checkbox
                isChecked={restrictCountry}
                onChange={(e) => setRestrictCountry(e.target.checked)}
                colorScheme="teal"
                color="whiteAlpha.900"
              >
                Restrict connection to
              </Checkbox>
              <Text fontSize="xs" color="whiteAlpha.500" mt={1} pl={6}>
                If enabled, VoraKit compares a <strong>detected-from-public-IP</strong> country (plus edge hints) to
                your selection before connecting. VPNs, proxies, or conflicting signals lower confidence — we never
                claim a verified country. A mismatch or unknown result blocks the library load for this server.
              </Text>
            </FormControl>
            {restrictCountry && (
              <FormControl isInvalid={Boolean(errors.connectionCountryCode)}>
                <FormLabel color="whiteAlpha.800" fontSize="sm">
                  Country
                </FormLabel>
                <Select
                  size="sm"
                  value={connectionCountryCode}
                  onChange={(e) => setConnectionCountryCode(e.target.value)}
                  placeholder="Select country"
                  bg="whiteAlpha.100"
                  borderColor="whiteAlpha.200"
                  color="white"
                  iconColor="whiteAlpha.600"
                  _focusVisible={{ borderColor: 'teal.400', boxShadow: '0 0 0 1px var(--chakra-colors-teal-400)' }}
                >
                  {COUNTRY_SELECT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.connectionCountryCode}</FormErrorMessage>
              </FormControl>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter gap={2} flexWrap="wrap">
          <Button variant="ghost" color="whiteAlpha.700" {...streamGhostOnDarkHover} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="outline"
            borderColor="whiteAlpha.300"
            color="white"
            {...streamOutlineOnDarkHover}
            onClick={() => handleSave(false)}
            isLoading={submitting}
          >
            Save server
          </Button>
          <Button colorScheme="teal" onClick={() => handleSave(true)} isLoading={submitting}>
            Save &amp; connect
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
